import { dynamicFormApiConnector } from 'apiConnector/form/dynamic';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of } from 'rxjs';
import { catchError, endWith, filter, mergeMap, takeUntil, timeoutWith } from 'rxjs/operators';
import { defaultFormReceivedThunk } from 'state/dynamic/dynamicForm.thunks';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import { workspaceTabCloneCompleted } from 'state/workspaces/workspace.actions';

import type { CloneFormRequest } from '../common-form/CloneFormRequest';

export interface CloneFormEpicApi {
  cloneForm(request: CloneFormRequest): Observable<DynamicFormResult>;
}
export function cloneDynamicFormEpic(timeoutInMillis: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient }) =>
    action$.pipe(
      ofType('WORKSPACE_TAB_CLONED'),
      filter(action => action.workspace === 'dynamic'),
      mergeMap(
        ({
          oldTabId,
          newTabId,
          clonedNegotiationId,
          workspace,
          tabType,
          productType,
          productSubtype: productSubType,
        }) => {
          const tabRemoved$ = tabRemoved(action$, newTabId, state$);
          return dynamicFormApiConnector(oldHttpClient)
            .cloneForm({ newTabId, clonedNegotiationId, productType, productSubType })
            .pipe(
              takeUntil(tabRemoved$),
              mergeMap(
                ({
                  blocksMetadata,
                  fieldsMetadata,
                  productDescription,
                  values,
                  version,
                  negotiationId,
                  actions,
                  quoteQuery,
                  errors,
                  sendWholeFormData,
                  allowedRequests,
                }) =>
                  of(
                    defaultFormReceivedThunk(
                      newTabId,
                      blocksMetadata,
                      fieldsMetadata,
                      productDescription,
                      values,
                      version,
                      negotiationId,
                      actions,
                      quoteQuery,
                      allowedRequests,
                      errors,
                      sendWholeFormData,
                    ),
                  ),
              ),
              catchError(error =>
                of(
                  commonFormActions.defaultFormFailed(
                    'dynamic',
                    newTabId,
                    error.message || 'clone failed',
                    error.traceId,
                  ),
                ),
              ),
              timeoutWith(
                timeoutInMillis,
                of(commonFormActions.defaultFormTimeouted('dynamic', newTabId)),
                appScheduler,
              ),
              endWith(workspaceTabCloneCompleted(oldTabId, workspace, tabType)),
            );
        },
      ),
    );
}
